import posthog from 'posthog-js'

import { isAnalyticsConfigured } from './config'
import { unregisterProperties } from './identity'

/**
 * Journey identity — the key that stitches one onboarding attempt together.
 *
 * BR-37 requires `journey_id` on every event, and the attempt spans separate
 * routes with no shared React state: the signup screens, then the wizard, then
 * the Done screen. So it needs its own store rather than living in a component.
 *
 * `localStorage` is authoritative and the value is *also* registered as a
 * PostHog super property, which is what puts it on every event without any call
 * site repeating it. The two can drift — `posthog.reset()` on logout clears
 * super properties but not this key — so `restoreJourneyId` re-registers at
 * boot.
 *
 * The server-side `OnboardingJourney.id` replaces this once `apps/onboarding`
 * exists; this value stays as the client-side join key for reconciliation.
 */

const KEY = 'skalx.analytics.journey'

const PROPERTY = 'journey_id'

type StoredJourney = {
  id: string
  /** ISO timestamp of the first screen, for `hours_since_start` on resume. */
  startedAt: string
}

const read = (): StoredJourney | null => {
  try {
    const raw = window.localStorage.getItem(KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as Partial<StoredJourney>

    return parsed.id
      ? { id: parsed.id, startedAt: parsed.startedAt ?? '' }
      : null
  } catch {
    return null
  }
}

const write = (journey: StoredJourney): void => {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(journey))
  } catch {
    /* private mode / quota — the journey then reads as a fresh one per page */
  }
}

const register = (id: string): void => {
  if (!isAnalyticsConfigured) return

  posthog.register({ [PROPERTY]: id })
}

/**
 * Returns the current journey, creating one if there is none.
 *
 * `created` is what tells `onboarding_started` apart from `onboarding_resumed` —
 * a distinction the funnel depends on, since a resume must not be counted as a
 * second person entering the top of it.
 */
export const getOrCreateJourney = (): {
  id: string
  startedAt: string
  created: boolean
} => {
  const existing = read()
  if (existing) {
    // Re-registered even when it already existed: a `reset()` since the last
    // page load would have dropped the super property while this key survived.
    register(existing.id)

    return { ...existing, created: false }
  }

  const journey: StoredJourney = {
    id: crypto.randomUUID(),
    startedAt: new Date().toISOString()
  }
  write(journey)
  register(journey.id)

  return { ...journey, created: true }
}

/** The current journey without creating one. */
export const readJourney = (): StoredJourney | null => read()

/**
 * Restores the super property at app boot, without minting a journey for
 * someone who is not onboarding.
 */
export const restoreJourneyId = (): void => {
  const existing = read()
  if (existing) register(existing.id)
}

/**
 * Ends the journey. Called on `onboarding_completed`, so events from ordinary
 * product use afterwards are not tagged with a finished onboarding attempt.
 *
 * `scope` and `entry` go with it: they describe the journey, and left registered
 * they would make a later sign-in read as part of a core onboarding that is over.
 */
export const clearJourney = (): void => {
  try {
    window.localStorage.removeItem(KEY)
  } catch {
    /* nothing to clean up */
  }

  if (!isAnalyticsConfigured) return

  posthog.unregister(PROPERTY)
  unregisterProperties(['scope', 'entry'])
}

/**
 * Milliseconds since the journey started, or null when the stored journey
 * predates timestamps. Used by `onboarding_completed`.
 */
export const msSince = (startedAt: string): number | null => {
  if (!startedAt) return null

  const started = Date.parse(startedAt)
  if (Number.isNaN(started)) return null

  return Math.max(0, Date.now() - started)
}
